import React from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { RefreshCw } from "lucide-react";

const PULL_THRESHOLD = 72;
const MAX_PULL = 118;

function isInteractiveTarget(target) {
  return Boolean(target?.closest?.("input, textarea, select, button, a, [role='button'], [data-ptr-ignore]"));
}

export default function PullToRefresh({ onRefresh, children, topOffset = 76 }) {
  const [pullDistance, setPullDistance] = React.useState(0);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const touchStartY = React.useRef(0);
  const pulling = React.useRef(false);
  const reducedMotion = useReducedMotion();

  React.useEffect(() => {
    const refresh = onRefresh || (() => window.location.reload());

    const handleTouchStart = (event) => {
      if (isRefreshing || isInteractiveTarget(event.target) || window.scrollY > 0) return;
      touchStartY.current = event.touches[0].clientY;
      pulling.current = true;
    };

    const handleTouchMove = (event) => {
      if (!pulling.current || isRefreshing) return;
      const distance = event.touches[0].clientY - touchStartY.current;
      if (distance <= 0 || window.scrollY > 0) {
        setPullDistance(0);
        pulling.current = false;
        return;
      }
      event.preventDefault();
      setPullDistance(Math.min(MAX_PULL, distance * 0.56));
    };

    const handleTouchEnd = async () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (pullDistance >= PULL_THRESHOLD) {
        setIsRefreshing(true);
        setPullDistance(PULL_THRESHOLD);
        await refresh();
        setIsRefreshing(false);
      }
      setPullDistance(0);
    };

    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    window.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [isRefreshing, onRefresh, pullDistance]);

  return (
    <>
      <AnimatePresence>
        {(pullDistance > 0 || isRefreshing) && (
          <motion.div
            role="status"
            aria-live="polite"
            aria-label={isRefreshing ? "Refreshing content" : "Pull to refresh"}
            initial={reducedMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1, y: Math.max(0, pullDistance - 40) }}
            exit={reducedMotion ? undefined : { opacity: 0, scale: 0.9 }}
            transition={{ duration: reducedMotion ? 0 : 0.18, ease: "easeOut" }}
            className="pointer-events-none fixed left-1/2 z-[70] grid h-11 w-11 -translate-x-1/2 place-items-center rounded-full bg-white/95 text-primary shadow-xl ring-1 ring-slate-200/80 backdrop-blur-xl md:hidden"
            style={{ top: `calc(${topOffset}px + env(safe-area-inset-top, 0px))` }}
          >
            <motion.span
              animate={{ rotate: isRefreshing ? 360 : (pullDistance / PULL_THRESHOLD) * 220 }}
              transition={{ duration: isRefreshing ? 0.9 : 0, repeat: isRefreshing && !reducedMotion ? Infinity : 0, ease: "linear" }}
            >
              <RefreshCw className="h-5 w-5" aria-hidden="true" />
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>
      {children}
    </>
  );
}